/* eslint-disable camelcase */
/* eslint-disable no-param-reassign */
import _ from 'lodash';
import moment from 'moment';
import * as types from '@/store/types';
// eslint-disable-next-line import/no-cycle
import lsOrderService from '@/services/order/order.service';
import { serviceIdToInstantQuote } from '@/constants';

// eslint-disable-next-line no-underscore-dangle
export const _private = {
    lastAPIRequest: null,
};

export const state = {
    quotes: [],
    instant_quotes: [],
    services: [],
    loadingStatus: {
        calculatingTotal: false,
    },
};

export const mutations = {
    /**
     * For updating the full state, successful api responses / order initialization
     * @param localState
     * @param quotes
     * @param services
     */
    [types.CART__MUTATE_CART](localState, { quotes, instant_quotes, services }) {
        localState.quotes = quotes;
        localState.instant_quotes = instant_quotes;
        localState.services = services;
    },

    /**
     * For updating cart loading status values
     * @param localState
     * @param service
     */
    [types.CART__MUTATE_CART_LOADING_STATUS](localState, payload) {
        _.assign(localState.loadingStatus, payload);
    },

    /**
     * Add a quote to the quotes array in the cart with the same structure as
     * what will be returned by the API
     * @param localState
     * @param service
     */
    [types.CART__MUTATE_CART_ADD_QUOTE](localState, service) {
        const quoteAlreadyInCart = _.some(
            localState.quotes,
            (quote) => _.get(quote, 'service.service_id') === service.service_id
        );
        if (quoteAlreadyInCart) {
            return;
        }

        localState.quotes.push({ service });
    },

    /**
     * Removes a quote from the quotes array in the cart by looking at service_id
     * @param localState
     * @param service_id
     */
    [types.CART__MUTATE_CART_REMOVE_QUOTE](localState, { service_id }) {
        localState.quotes = _.filter(localState.quotes, (quote) => {
            const quoteServiceId = _.get(quote, 'service.service_id');
            return quoteServiceId !== service_id;
        });
    },

    /**
     * Add a insatantQuote to the quotes array in the cart with the same structure as
     * what will be returned by the API
     * @param localState
     * @param service
     */
    [types.CART__MUTATE_CART_ADD_INSTANT_QUOTE](localState, instantQuote) {
        const quoteAlreadyInCart = _.some(
            localState.instant_quotes,
            (quote) => _.get(quote, 'id') === instantQuote.id
        );

        if (quoteAlreadyInCart) {
            return;
        }

        // always return sorted iqs to avoid the OrderSummery from swapping positions when quickly adding iqs
        localState.instant_quotes = _.sortBy(
            [...localState.instant_quotes, instantQuote],
            ['type']
        );
    },

    /**
     * Removes an instantQuote from the instant_quotes array in the cart by looking at id
     * @param localState
     * @param instantQuote
     */

    [types.CART__MUTATE_CART_REMOVE_INSTANT_QUOTE](localState, { instantQuoteId }) {
        localState.instant_quotes = _.reject(localState.instant_quotes, { id: instantQuoteId });
    },

    /**
     * Add a priced service to the services array in the cart with the same structure as
     * what will be returned by the API
     * @param localState
     * @param price
     * @param service
     */
    [types.CART__MUTATE_CART_ADD_SERVICE](localState, { price, service }) {
        const formattedOrderService = { price: _.cloneDeep(price) };
        formattedOrderService.price.service = _.cloneDeep(service);

        // guard against duplicates of same priced service
        localState.services = _.filter(
            localState.services,
            (pricedService) =>
                _.get(pricedService, 'price.service.service_id') !== service.service_id
        );

        localState.services.push(formattedOrderService);
    },

    /**
     * Removes a service from services array in the cart by looking at price_id
     * @param localState
     * @param price
     */
    [types.CART__MUTATE_CART_REMOVE_SERVICE](localState, { price }) {
        localState.services = _.filter(localState.services, (pricedService) => {
            const pricedServicePriceId = _.get(pricedService, 'price.price_id');
            return pricedServicePriceId !== price.price_id;
        });
    },
};

export const actions = {
    [types.CART__UPDATE_CART]({ commit }, { quotes, instant_quotes, services }) {
        commit(types.CART__MUTATE_CART, { quotes, instant_quotes, services });
    },
    [types.CART__ADD_QUOTE_TO_CART]({ commit, rootState, dispatch }, service) {
        const uniqueRequestIdentifier = moment();
        _private.lastAPIRequest = uniqueRequestIdentifier;

        commit(types.CART__MUTATE_CART_ADD_QUOTE, service);
        lsOrderService
            .addQuote(rootState.order, service)
            .then((response) => {
                if (uniqueRequestIdentifier === _private.lastAPIRequest) {
                    dispatch(types.ORDER__UPDATE_ORDER, { cart: _.get(response, 'data.cart') });
                }
            })
            .catch(() => {
                // reset mutation as it wasn't successful
                commit(types.CART__MUTATE_CART_REMOVE_QUOTE, service);
            });
    },

    [types.CART__REMOVE_QUOTE_FROM_CART]({ commit, rootState, dispatch }, service) {
        const uniqueRequestIdentifier = moment();
        _private.lastAPIRequest = uniqueRequestIdentifier;

        commit(types.CART__MUTATE_CART_REMOVE_QUOTE, service);

        lsOrderService
            .removeByServiceIds(rootState.order, service)
            .then((response) => {
                if (uniqueRequestIdentifier === _private.lastAPIRequest) {
                    dispatch(types.ORDER__UPDATE_ORDER, { cart: _.get(response, 'data.cart') });
                }
            })
            .catch((err) => {
                // reset mutation as it wasn't successful
                commit(types.CART__MUTATE_CART_ADD_QUOTE, service);
                dispatch(types.ERROR__SET_ERROR, err);
            });
    },

    [types.CART__ADD_INSTANT_QUOTE_TO_CART]({ commit, dispatch, rootState }, service_id) {
        const uniqueRequestIdentifier = moment();
        _private.lastAPIRequest = uniqueRequestIdentifier;

        const { type, dummyId, endpointSuffix } = serviceIdToInstantQuote[service_id];

        // add a dummy object here so there is something until the response is returned
        const temporaryOptimisticInstantQuote = {
            id: dummyId,
            type,
        };
        commit(types.CART__MUTATE_CART_ADD_INSTANT_QUOTE, temporaryOptimisticInstantQuote);

        lsOrderService
            .addInstantQuote(rootState.order, endpointSuffix)
            .then((response) => {
                // remove dummy object on api success
                commit(types.CART__MUTATE_CART_REMOVE_INSTANT_QUOTE, {
                    instantQuoteId: dummyId,
                });
                commit(types.CART__MUTATE_CART_ADD_INSTANT_QUOTE, response.data);

                if (uniqueRequestIdentifier === _private.lastAPIRequest) {
                    // ultimately we should update the order with the respose once it returns the order
                    const cart = {
                        ...rootState.order.cart,
                        quotes: state.quotes,
                        // in case current state of the cart has a dummy id from a different call,
                        // remove ALL dummyIds listed in serviceIdToInstantQuote constants
                        instant_quotes: _.reject(state.instant_quotes, {
                            id: 'dummyBushId' || 'dummyLeafId',
                        }),
                        services: state.services,
                    };
                    dispatch(types.ORDER__UPDATE_ORDER, {
                        cart,
                    });
                }
            })
            .catch((err) => {
                // reset mutation as it wasn't successful
                commit(types.CART__MUTATE_CART_REMOVE_INSTANT_QUOTE, {
                    instantQuoteId: dummyId,
                });
                dispatch(types.ERROR__SET_ERROR, err);
            });
    },

    [types.CART__REMOVE_INSTANT_QUOTE_FROM_CART]({ commit, rootState, dispatch }, instantQuote) {
        const uniqueRequestIdentifier = moment();
        _private.lastAPIRequest = uniqueRequestIdentifier;

        commit(types.CART__MUTATE_CART_REMOVE_INSTANT_QUOTE, { instantQuoteId: instantQuote.id });

        // do not try to call reject endpoint with dummyId
        if (instantQuote.id !== 'dummyBushId' && instantQuote.id !== 'dummyLeafId') {
            lsOrderService
                .rejectInstantQuote(rootState.order, instantQuote.id)
                .then((response) => {
                    commit(types.CART__MUTATE_CART_REMOVE_INSTANT_QUOTE, {
                        instantQuoteId: response.data.id,
                    });
                    if (uniqueRequestIdentifier === _private.lastAPIRequest) {
                        // ultimately we should update the order with the respose once it returns the order
                        const cart = {
                            ...rootState.order.cart,
                            quotes: state.quotes,
                            // in case current state of the cart has a dummy id from a different call,
                            // remove ALL dummyIds listed in serviceIdToInstantQuote constants
                            instant_quotes: _.reject(state.instant_quotes, {
                                id: 'dummyBushId' || 'dummyLeafId',
                            }),
                            services: state.services,
                        };
                        dispatch(types.ORDER__UPDATE_ORDER, {
                            cart,
                        });
                    }
                })
                .catch((err) => {
                    commit(types.CART__MUTATE_CART_ADD_INSTANT_QUOTE, instantQuote);
                    dispatch(types.ERROR__SET_ERROR, err);
                });
        } else {
            // make sure if it is the dummy id, we still remove it from the local state
            commit(types.CART__MUTATE_CART_REMOVE_INSTANT_QUOTE, {
                instantQuoteId: instantQuote.id,
            });
        }
    },

    [types.CART__ADD_SERVICE_TO_CART]({ commit, rootState, dispatch }, { price, service }) {
        const uniqueRequestIdentifier = moment();

        _private.lastAPIRequest = uniqueRequestIdentifier;
        commit(types.CART__MUTATE_CART_ADD_SERVICE, { price, service });

        if (service.str_id === 'mowing') {
            commit(types.CART__MUTATE_CART_LOADING_STATUS, { calculatingTotal: true });
        }

        lsOrderService
            .addService(rootState.order, price)
            .then((response) => {
                if (uniqueRequestIdentifier === _private.lastAPIRequest) {
                    dispatch(types.ORDER__UPDATE_ORDER, { cart: _.get(response, 'data.cart') });

                    if (service.str_id === 'mowing') {
                        commit(types.CART__MUTATE_CART_LOADING_STATUS, { calculatingTotal: false });
                    }
                }
            })
            .catch((err) => {
                // reset mutation as it wasn't successful
                commit(types.CART__MUTATE_CART_REMOVE_SERVICE, { price, service });
                dispatch(types.ERROR__SET_ERROR, err);
            });
    },

    [types.CART__REMOVE_SERVICE_FROM_CART]({ commit, rootState, dispatch }, { price, service }) {
        const uniqueRequestIdentifier = moment();

        _private.lastAPIRequest = uniqueRequestIdentifier;
        commit(types.CART__MUTATE_CART_REMOVE_SERVICE, { price, service });

        lsOrderService
            .removeByServiceIds(rootState.order, service)
            .then((response) => {
                if (uniqueRequestIdentifier === _private.lastAPIRequest) {
                    dispatch(types.ORDER__UPDATE_ORDER, { cart: _.get(response, 'data.cart') });
                }
            })
            .catch((err) => {
                // reset mutation as it wasn't successful
                commit(types.CART__MUTATE_CART_ADD_SERVICE, { price, service });
                dispatch(types.ERROR__SET_ERROR, err);
            });
    },
};

export const getters = {
    [types.CART__GET_PRICED_LAWNTREATMENT_IS_IN_CART](localState) {
        return _.some(
            localState.services,
            (orderService) => _.get(orderService, 'price.service.service_id') === 2
        );
    },

    [types.CART__GET_NON_MOWING_SERVICES_IN_CART](localState) {
        return _.filter(
            localState.services,
            (orderService) => _.get(orderService, 'price.service.service_id') !== 1
        );
    },

    [types.ORDER__GET_CART_TREATMENT_TOTAL](localState) {
        return _.filter(
            localState.services,
            (orderService) => _.get(orderService, 'price.service.service_id') === 2
        ).reduce((total, val) => total + val.price.price, 0);
    },

    [types.CART__GET_MOWING_IN_CART](localState) {
        return _.find(
            localState.services,
            (orderService) => _.get(orderService, 'price.service.service_id') === 1
        );
    },

    [types.CART__GET_QUOTES_IN_CART](localState) {
        return localState.quotes;
    },

    [types.CART__GET_QUOTES_AND_INSTANT_QUOTES_SERVICE_OBJECTS_IN_CART](localState) {
        const quoteServiceObjects = _.map(localState.quotes, 'service');
        const instantQuoteServiceObjects = _.map(localState.instant_quotes, (iq) => {
            if (iq.type === 'instant_quote_bush_trimming') {
                return {
                    service_id: 8,
                    name: 'Bush Trimming',
                };
            }
            return {
                service_id: 10,
                name: 'Leaf Removal',
            };
        });
        return _.concat(instantQuoteServiceObjects, quoteServiceObjects);
    },
};
